import publicApi from './publicApi';
import api from './api';

/**
 * Get appointments (staff see all, riders see own). Requires auth.
 */
export const getAppointments = async () => {
  try {
    const response = await api.get('/appointments');
    const appointments = Array.isArray(response.data) ? response.data : [];
    appointments.sort((a, b) => {
      const dateA = new Date(a.appointment_date);
      const dateB = new Date(b.appointment_date);
      return dateB - dateA;
    });
    return { success: true, data: appointments };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to fetch appointments',
      data: [],
    };
  }
};

/**
 * Get providers (auth). For staff appointment list provider names.
 */
export const getProviders = async () => {
  try {
    const response = await api.get('/providers');
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to fetch providers',
      data: [],
    };
  }
};

/**
 * Update an appointment (auth).
 */
export const updateAppointment = async (appointmentId, payload) => {
  try {
    await api.put(`/appointments/${appointmentId}`, payload);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to update appointment',
    };
  }
};

/**
 * Cancel/delete an appointment (auth).
 */
export const cancelAppointment = async (appointmentId) => {
  try {
    await api.delete(`/appointments/${appointmentId}`);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to cancel appointment',
    };
  }
};

/**
 * Public booking (no login): get providers for new participants
 */
export const getPublicProviders = async () => {
  try {
    const response = await publicApi.get('/public/providers');
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to fetch providers',
      data: [],
    };
  }
};

/**
 * Public booking (no login): get provider availability
 */
export const getPublicProviderAvailability = async (providerId, location = null) => {
  try {
    const params = {};
    if (location) {
      params.location = location;
    }
    const response = await publicApi.get(`/public/providers/${providerId}/availability`, { params });
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to fetch availability',
      data: [],
    };
  }
};

/**
 * Public booking (no login): create appointment (new participant, no user_id)
 */
export const createPublicAppointment = async (appointmentData) => {
  try {
    const requiredFields = ['service', 'appointment_date', 'appointment_time', 'first_name', 'last_name', 'email', 'is_employee'];
    for (const field of requiredFields) {
      if (!appointmentData[field]) {
        return { success: false, message: `Missing required field: ${field}` };
      }
    }
    const response = await publicApi.post('/public/appointments', {
      service: appointmentData.service,
      provider_id: appointmentData.provider_id || null,
      appointment_date: appointmentData.appointment_date,
      appointment_time: appointmentData.appointment_time,
      first_name: appointmentData.first_name,
      last_name: appointmentData.last_name,
      email: appointmentData.email,
      phone: appointmentData.phone || '',
      is_employee: appointmentData.is_employee,
    });
    return { success: true, data: response.data, message: 'Appointment created successfully' };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Failed to create appointment',
    };
  }
};
